import React from 'react';
import { Box } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { subMonths } from 'date-fns';
import { FILTER_HEIGHT } from './ui/filterStyles';

export interface DateRangeFilterProps {
  startDate: Date | null;
  endDate: Date | null;
  onChange: (start: Date | null, end: Date | null) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
}

// Both pickers sit inside one outlined control so the range reads as a
// single filter: "08/03/2026 – 10/02/2026".
const pickerInputSx = {
  color: 'text.primary',
  width: '100%',
  fontSize: '0.875rem',
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
  '& .MuiInputBase-input': {
    cursor: 'pointer',
    p: 0,
    lineHeight: '1.5rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  '& .MuiIconButton-root': { p: 0.5, mr: -0.5 },
  '& .MuiSvgIcon-root': {
    color: 'text.secondary',
    fontSize: '1.1rem',
    flexShrink: 0,
  },
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate,
  endDate,
  onChange,
  minDate,
  maxDate,
}) => {
  // Calculate default min/max dates if not provided
  const defaultMinDate = minDate || subMonths(new Date(), 36); // 3 years ago
  const defaultMaxDate = maxDate || new Date(); // today

  const handleStartDateChange = (date: Date | null) => {
    onChange(date, endDate);
  };

  const handleEndDateChange = (date: Date | null) => {
    onChange(startDate, date);
  };

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        height: FILTER_HEIGHT,
        px: 1.5,
        gap: 1,
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        transition: 'border-color 120ms ease',
        '&:hover': { borderColor: theme.palette.text.secondary },
        '&:focus-within': { borderColor: theme.palette.primary.main, boxShadow: `inset 0 0 0 0.5px ${theme.palette.primary.main}` },
      })}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <DatePicker
            value={startDate}
            onChange={handleStartDateChange}
            maxDate={endDate || defaultMaxDate}
            minDate={defaultMinDate}
            format="MM/dd/yyyy"
            slotProps={{
              textField: {
                variant: 'standard',
                inputProps: { 'aria-label': 'Start date' },
                InputProps: { disableUnderline: true, sx: pickerInputSx },
              },
            }}
          />
        </Box>
        <Box component="span" aria-hidden sx={{ color: 'text.secondary', flexShrink: 0 }}>
          &ndash;
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <DatePicker
            value={endDate}
            onChange={handleEndDateChange}
            minDate={startDate || defaultMinDate}
            maxDate={defaultMaxDate}
            format="MM/dd/yyyy"
            slotProps={{
              textField: {
                variant: 'standard',
                inputProps: { 'aria-label': 'End date' },
                InputProps: { disableUnderline: true, sx: pickerInputSx },
              },
            }}
          />
        </Box>
      </LocalizationProvider>
    </Box>
  );
};

export default DateRangeFilter;
